/* eslint-disable sort-imports */
/**
 * One phase (crew line) of an operation in the form: phase type, job role,
 * crew size, timing, notes, and the Deferrable flag.
 */
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
	Box,
	Checkbox,
	FormControl,
	FormControlLabel,
	Grid2,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import React, { useCallback } from "react";

import { WorkPhase } from "../../../../services/work-phases-api";
import { planner, roleColor } from "../../planner-ui";
import { parseCrew,PhaseFormRow } from "../utils/standard-helpers";
import { deleteIconButtonSx, fieldSx, menuItemSx, monoFieldSx } from "./styles";

export interface MasterJob {
	id: number;
	name: string;
	// Master jobs carry a per-job hexColor, but the design system owns role
	// colour: roleColor() from planner-ui is used everywhere instead.
	hexColor?: string;
}

export type PhaseFieldValue = string | number | boolean | null;

export interface PhaseRowProps {
	opIndex: number;
	phaseIndex: number;
	phase: PhaseFormRow;
	jobRoles: MasterJob[];
	workPhases: WorkPhase[];
	/** Disabled when it is the operation's only phase. */
	canRemove: boolean;
	onChange: (opIndex: number, phaseIndex: number, field: keyof PhaseFormRow, value: PhaseFieldValue) => void;
	onRemove: (opIndex: number, phaseIndex: number) => void;
}

const PhaseRow: React.FC<PhaseRowProps> = ({ opIndex, phaseIndex, phase, jobRoles, workPhases, canRemove, onChange, onRemove }) => {
	const crew = parseCrew(phase.workersRequired) ?? 1;

	const handleWorkPhase = useCallback(
		(e: SelectChangeEvent<number | "">) => {
			const raw = e.target.value;
			onChange(opIndex, phaseIndex, "workPhaseId", raw === "" ? null : Number(raw));
		},
		[onChange, opIndex, phaseIndex]
	);
	const handleJobRole = useCallback(
		(e: SelectChangeEvent<number | "">) => {
			const raw = e.target.value;
			const selectedId = raw === "" ? null : (raw as number);
			const selectedJob = jobRoles.find((jr) => jr.id === selectedId);
			// Update both jobRoleId and jobRole (name)
			onChange(opIndex, phaseIndex, "jobRoleId", selectedId);
			onChange(opIndex, phaseIndex, "jobRole", selectedJob?.name || "");
		},
		[onChange, opIndex, phaseIndex, jobRoles]
	);
	const handleWorkers = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => onChange(opIndex, phaseIndex, "workersRequired", e.target.value),
		[onChange, opIndex, phaseIndex]
	);
	const handleStartOffset = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => onChange(opIndex, phaseIndex, "startOffsetMinutes", e.target.value),
		[onChange, opIndex, phaseIndex]
	);
	const handleDuration = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => onChange(opIndex, phaseIndex, "durationMinutes", e.target.value),
		[onChange, opIndex, phaseIndex]
	);
	const handleNotes = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => onChange(opIndex, phaseIndex, "notes", e.target.value),
		[onChange, opIndex, phaseIndex]
	);
	const handleDeferrable = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => onChange(opIndex, phaseIndex, "isDeferrable", e.target.checked),
		[onChange, opIndex, phaseIndex]
	);
	const handleRemove = useCallback(() => onRemove(opIndex, phaseIndex), [onRemove, opIndex, phaseIndex]);

	return (
		<Box
			sx={{
				p: 1.5,
				mb: 1,
				bgcolor: planner.surfaceSubtle,
				border: `1px solid ${planner.hairline}`,
				borderRadius: `${planner.radiusSm}px`,
			}}
		>
			<Grid2 container spacing={1} alignItems="center">
				{/* Phase type: the company's own Work Phases vocabulary. */}
				<Grid2 size={{ xs: 12, sm: 2 }}>
					<FormControl fullWidth size="small" sx={fieldSx}>
						<InputLabel>Phase type</InputLabel>
						<Select value={phase.workPhaseId ?? ""} onChange={handleWorkPhase} label="Phase type">
							<MenuItem value="" sx={menuItemSx}>
								<Box component="span" sx={{ color: planner.inkMuted }}>
									None
								</Box>
							</MenuItem>
							{workPhases
								.filter((wp) => wp.isActive || wp.id === phase.workPhaseId)
								.map((wp) => (
									<MenuItem key={wp.id} value={wp.id} sx={menuItemSx}>
										<Box component="span" sx={{ fontFamily: planner.fontMono, color: planner.accentDeep, mr: 0.75 }}>
											{wp.code}
										</Box>
										{wp.name}
									</MenuItem>
								))}
						</Select>
					</FormControl>
				</Grid2>
				{/* Job role is required for a crew; machine time (0 workers) has none. */}
				<Grid2 size={{ xs: 12, sm: 2.5 }}>
					<FormControl fullWidth size="small" required={crew > 0} sx={fieldSx}>
						<InputLabel>Job Role</InputLabel>
						<Select value={phase.jobRoleId ?? ""} onChange={handleJobRole} label="Job Role">
							{crew === 0 ? (
								<MenuItem value="" sx={menuItemSx}>
									<Box component="span" sx={{ color: planner.inkMuted }}>
										None — machine time
									</Box>
								</MenuItem>
							) : null}
							{jobRoles.map((jr) => (
								<MenuItem key={jr.id} value={jr.id} sx={menuItemSx}>
									<Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: roleColor(jr.name), mr: 1, flexShrink: 0 }} />
									{jr.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid2>
				<Grid2 size={{ xs: 6, sm: 1.5 }}>
					{/* 0 = machine time (the unit is busy, nobody is needed);
					    decimals such as 0.5 or 2.67 are fractional crews. */}
					<Tooltip title="0 = machine time: the phase occupies the unit but needs nobody. Decimals (0.5, 2.67) are fractional crews.">
						<TextField
							fullWidth
							size="small"
							label="Workers"
							type="number"
							value={phase.workersRequired}
							onChange={handleWorkers}
							inputProps={{ min: 0, step: "any" }}
							required
							sx={monoFieldSx}
						/>
					</Tooltip>
				</Grid2>
				<Grid2 size={{ xs: 6, sm: 1.5 }}>
					<TextField
						fullWidth
						size="small"
						label="Start Offset (min)"
						type="number"
						value={phase.startOffsetMinutes}
						onChange={handleStartOffset}
						inputProps={{ min: 0 }}
						sx={monoFieldSx}
					/>
				</Grid2>
				<Grid2 size={{ xs: 6, sm: 1.5 }}>
					<TextField
						fullWidth
						size="small"
						label="Duration (min)"
						type="number"
						value={phase.durationMinutes}
						onChange={handleDuration}
						inputProps={{ min: 1 }}
						required
						sx={monoFieldSx}
					/>
				</Grid2>
				<Grid2 size={{ xs: 6, sm: 2 }}>
					<TextField fullWidth size="small" label="Notes" value={phase.notes} onChange={handleNotes} sx={fieldSx} />
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 1 }}>
					<IconButton size="small" onClick={handleRemove} disabled={!canRemove} sx={deleteIconButtonSx}>
						<DeleteIcon fontSize="small" />
					</IconButton>
				</Grid2>

				{/* Scheduling flexibility. Short sign-offs (inspections) can wait
				    for a free pair of hands; work that occupies the unit cannot. */}
				<Grid2 size={{ xs: 12 }}>
					<Tooltip title="The phase time becomes the earliest it may start. If nobody qualified is free, the allocator schedules it later instead of leaving it unstaffed. Use for short sign-offs, not for work that holds the unit.">
						<FormControlLabel
							sx={{ ml: 0 }}
							control={
								<Checkbox
									size="small"
									checked={Boolean(phase.isDeferrable)}
									onChange={handleDeferrable}
									sx={{ color: planner.inkMuted, "&.Mui-checked": { color: planner.accent } }}
								/>
							}
							label={<Typography sx={{ ...planner.type.meta, color: planner.inkSecondary }}>Deferrable</Typography>}
						/>
					</Tooltip>
				</Grid2>
			</Grid2>
		</Box>
	);
};

export default PhaseRow;
